import React, { createContext, useContext, useState, useCallback } from 'react';
import { attendanceAPI, api } from '../services/api';
import { cachedRequest } from '../utils/apiUtils';
import toast from 'react-hot-toast';

const AttendanceContext = createContext();

export const useAttendance = () => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error('useAttendance must be used within an AttendanceProvider');
  }
  return context;
};

export const AttendanceProvider = ({ children }) => {
  const [attendanceList, setAttendanceList] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all attendance records with caching
  const fetchAttendance = useCallback(async (filters = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await cachedRequest(
        () => attendanceAPI.getAllAttendance(filters),
        '/attendence',
        filters
      );
      setAttendanceList(response.data?.attendance || []);
      return response.data?.attendance || [];
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch attendance data';
      setError(errorMessage);
      console.error('Error fetching attendance:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch attendance by employee
  const fetchAttendanceByEmployee = useCallback(async (employeeId, filters = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await attendanceAPI.getAttendanceByEmployee(employeeId, filters);
      return response.data?.attendance || [];
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch employee attendance';
      setError(errorMessage);
      console.error('Error fetching employee attendance:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch all managers for filtering with caching
  const fetchManagers = useCallback(async () => {
    try {
      const response = await cachedRequest(
        () => api.get('/manager/all'),
        '/manager/all'
      );
      setManagers(response.data?.data || response.data || []);
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error('Error fetching managers:', err);
      return [];
    }
  }, []);

  // Fetch all employees for filtering with caching
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await cachedRequest(
        () => api.get('/employee/all'),
        '/employee/all'
      );
      setEmployees(response.data?.data || response.data || []);
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error('Error fetching employees:', err);
      return [];
    }
  }, []);

  // Fetch location-wise attendance data
  const fetchLocationWiseAttendance = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/attendence/locationwise');
      return response.data?.data || [];
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch location-wise attendance data';
      setError(errorMessage);
      console.error('Error fetching location-wise attendance:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  // Update single attendance record
  const updateAttendance = useCallback(async (id, data) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await attendanceAPI.updateAttendance(id, data);
      console.log('AttendanceContext - Update response:', response.data);
      
      // Update the record in the list - backend returns { message, attendance }
      const updatedRecord = response.data.attendance || response.data;
      console.log('Updating attendance list with:', { id, updatedRecord });
      
      setAttendanceList(prev => {
        const newList = prev.map(record => {
          if (record._id === id) {
            console.log('Found matching record, updating:', record._id, 'with:', updatedRecord);
            return { ...record, ...updatedRecord };
          }
          return record;
        });
        console.log('New attendance list:', newList);
        return newList;
      });
      return updatedRecord;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update attendance';
      setError(errorMessage);
      console.error('Error updating attendance:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  // Bulk update attendance records
  const bulkUpdateAttendance = useCallback(async (attendanceIds, updates) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await attendanceAPI.bulkUpdateAttendance({
        attendanceIds,
        updates
      });
      // Refresh the attendance list
      await fetchAttendance();
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to bulk update attendance';
      setError(errorMessage);
      console.error('Error bulk updating attendance:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchAttendance, setError]);

  // Export attendance data to CSV
  const exportToCSV = useCallback((data, filename = 'attendance_report.csv') => {
    if (!data || data.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      // Add header information
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      const presentCount = data.filter(record => record.stepIn).length;
      const absentCount = data.filter(record => !record.stepIn).length;
      const totalHours = data.reduce((sum, record) => sum + (record.totalTime || 0), 0);

      const headerInfo = [
        ['PANTHER SECURE - ATTENDANCE REPORT'],
        [''],
        [`Report Generated: ${reportDate}`],
        [`Total Records: ${data.length}`],
        [`Present: ${presentCount} | Absent: ${absentCount} | Total Hours: ${Math.floor(totalHours / 60)}h ${totalHours % 60}m`],
        ['']
      ];

      // Define CSV headers
      const headers = [
        'Date',
        'Employee Name',
        'Employee Email',
        'Shift',
        'Clock In',
        'Clock Out',
        'Location',
        'Status'
      ];

      // Convert data to CSV format
      const csvData = data.map(record => [
        new Date(record.stepIn).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
        record.employeeId?.name || 'N/A',
        record.employeeId?.email || 'N/A',
        record.shift?.toUpperCase() || 'N/A',
        record.stepIn ? new Date(record.stepIn).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A',
        record.stepOut ? new Date(record.stepOut).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A',
        record.address || 'N/A',
        record.stepIn ? 'Present' : 'Present'
      ]);

      // Create CSV content with header info
      const csvContent = [
        ...headerInfo.map(row => row.join(',')),
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      // Create blob
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Check if in WebView and use appropriate download method
      if (window.ReactNativeWebView) {
        console.log('📱 WebView detected, using native CSV download...');
        
        // Convert blob to base64 for WebView
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          
          // Send to React Native using the exact format your app expects
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'download',
            fileType: 'csv',
            fileName: filename,
            data: base64Data
          }));
          
          console.log('📊 CSV sent to React Native:', filename);
        };
        reader.readAsDataURL(blob);
      } else {
        // Fallback for regular browser
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Failed to export CSV');
      console.error('Error exporting CSV:', err);
    }
  }, []);

  // Export attendance data to PDF
  const exportToPDF = useCallback(async (data, filename = 'attendance_report.pdf') => {
    if (!data || data.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Page dimensions
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);

      // Colors
      const primaryColor = [41, 128, 185]; // Blue
      const secondaryColor = [52, 73, 94]; // Dark gray
      const accentColor = [46, 204, 113]; // Green
      const dangerColor = [231, 76, 60]; // Red
      const lightGray = [248, 249, 250]; // Light gray for alternating rows

      // Helper function to add text with custom styling
      const addText = (text, x, y, fontSize = 12, fontStyle = 'normal', color = secondaryColor) => {
        doc.setTextColor(...color);
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontStyle);
        doc.text(text, x, y);
      };

      // Helper function to draw rectangle
      const drawRect = (x, y, width, height, color = primaryColor, fill = false) => {
        doc.setDrawColor(...color);
        doc.setFillColor(...color);
        if (fill) {
          doc.rect(x, y, width, height, 'F');
        } else {
          doc.rect(x, y, width, height);
        }
      };

      // Helper function to draw line
      const drawLine = (x1, y1, x2, y2, color = secondaryColor, width = 0.5) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(width);
        doc.line(x1, y1, x2, y2);
      };

      // Helper function to right align text
      const rightAlignText = (text, x, y, fontSize = 12, fontStyle = 'normal', color = secondaryColor) => {
        const textWidth = doc.getTextWidth(text);
        addText(text, x - textWidth, y, fontSize, fontStyle, color);
      };

      let currentPage = 1;
      let y = margin;

      // Function to add a new page
      const addNewPage = () => {
        doc.addPage();
        currentPage++;
        y = margin;
        
        // Add page number
        const pageText = `Page ${currentPage}`;
        rightAlignText(pageText, pageWidth - margin, pageHeight - 10, 9, 'normal', [128, 128, 128]);
      };

      // Header Section
      const headerHeight = 35;
      
      // Company logo (actual image)
      try {
        // Load and add the logo image
        const logoImg = new Image();
        logoImg.src = '/assets/logo.png';
        
        // Wait for image to load
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          // Set a timeout in case the image doesn't load
          setTimeout(reject, 5000);
        });
        
        // Add the logo image to PDF
        doc.addImage(logoImg, 'PNG', margin, y, 25, 25);
      } catch (error) {
        console.warn('Could not load logo image, using fallback:', error);
        // Fallback to text logo if image fails to load
        drawRect(margin, y, 25, 25, primaryColor, true);
        addText('PS', margin + 12, y + 17, 14, 'bold', [255, 255, 255]);
        drawRect(margin, y, 25, 25, secondaryColor, false);
      }
      
      // Company name and title
      addText('PANTHER SECURE', margin + 35, y + 8, 16, 'bold', primaryColor);
      addText('Professional Security Solutions', margin + 35, y + 16, 9, 'normal', secondaryColor);
      addText('ATTENDANCE REPORT', margin + 35, y + 24, 12, 'bold', secondaryColor);
      
      // Report details
      const reportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      rightAlignText(`Generated: ${reportDate}`, pageWidth - margin, y + 8, 8, 'normal', secondaryColor);
      rightAlignText(`Total Records: ${data.length}`, pageWidth - margin, y + 16, 8, 'normal', secondaryColor);
      
      y += headerHeight + 8;

      // Summary Section
      const presentCount = data.filter(record => record.stepIn).length;
      const absentCount = data.filter(record => !record.stepIn).length;
      const totalHours = data.reduce((sum, record) => sum + (record.totalTime || 0), 0);
      
      // Draw summary box with border
      drawRect(margin, y, contentWidth, 22, [248, 249, 250], true);
      drawRect(margin, y, contentWidth, 22, secondaryColor, false);
      
      addText('SUMMARY', margin + 10, y + 7, 11, 'bold', secondaryColor);
      addText(`Present: ${presentCount}`, margin + 10, y + 15, 9, 'normal', accentColor);
      addText(`Absent: ${absentCount}`, margin + 70, y + 15, 9, 'normal', dangerColor);
      addText(`Total Hours: ${Math.floor(totalHours / 60)}h ${totalHours % 60}m`, margin + 130, y + 15, 9, 'normal', primaryColor);
      
      y += 28;

      // Table Header
      const tableHeaders = ['Date', 'Employee Name', 'Shift', 'Clock In', 'Clock Out', 'Status'];
      const colWidths = [22, 45, 18, 22, 22, 18];
      const colX = margin;
      
      // Draw table header background with border
      drawRect(colX, y, contentWidth, 12, primaryColor, true);
      drawRect(colX, y, contentWidth, 12, [255, 255, 255], false);
      
      // Add header text
      let currentX = colX + 2;
      tableHeaders.forEach((header, index) => {
        addText(header, currentX, y + 7, 9, 'bold', [255, 255, 255]);
        currentX += colWidths[index];
      });
      
      y += 14;

      // Table Data
      data.forEach((record, index) => {
        // Check if we need a new page
        if (y > pageHeight - 40) {
          addNewPage();
          
          // Redraw table header on new page
          drawRect(colX, y, contentWidth, 12, primaryColor, true);
          drawRect(colX, y, contentWidth, 12, [255, 255, 255], false);
          currentX = colX + 2;
          tableHeaders.forEach((header, headerIndex) => {
            addText(header, currentX, y + 7, 9, 'bold', [255, 255, 255]);
            currentX += colWidths[headerIndex];
          });
          y += 14;
        }

        // Alternate row colors with borders
        const rowColor = index % 2 === 0 ? lightGray : [255, 255, 255];
        drawRect(colX, y, contentWidth, 10, rowColor, true);
        drawRect(colX, y, contentWidth, 10, [220, 220, 220], false);
        
        // Add row data
        currentX = colX + 2;
        const rowData = [
          new Date(record.stepIn).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
          record.employeeId?.name || 'N/A',
          record.shift?.toUpperCase() || 'N/A',
          record.stepIn ? new Date(record.stepIn).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A',
          record.stepOut ? new Date(record.stepOut).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'N/A',
          record.stepIn ? 'Present' : 'Present'
        ];

        rowData.forEach((cell, cellIndex) => {
          const cellColor = cellIndex === 5 ? accentColor : secondaryColor;
          
          // Truncate long text to fit in column
          let displayText = cell;
          if (cellIndex === 1 && cell.length > 20) { // Employee name column
            displayText = cell.substring(0, 17) + '...';
          } else if (cell.length > 15) {
            displayText = cell.substring(0, 12) + '...';
          }
          
          addText(displayText, currentX, y + 6, 8, 'normal', cellColor);
          currentX += colWidths[cellIndex];
        });

        y += 12;
      });

      // Footer Section
      y += 10;
      drawLine(margin, y, pageWidth - margin, y, secondaryColor, 1);
      y += 10;
      
      addText('This report was generated automatically by Panther Secure System.', margin, y, 8, 'normal', [128, 128, 128]);
      addText('For any queries, please contact the system administrator.', margin, y + 5, 8, 'normal', [128, 128, 128]);
      
      // Add page number to first page
      rightAlignText('Page 1', pageWidth - margin, pageHeight - 10, 9, 'normal', [128, 128, 128]);

      // Check if running in WebView
      if (window.ReactNativeWebView) {
        console.log('📱 WebView detected, using native download...');
        
        // Get PDF as base64 data URI
        const pdfDataUri = doc.output('datauristring');
        
        // Send to React Native using the exact format your app expects
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'download',
          fileType: 'pdf',
          fileName: filename,
          data: pdfDataUri
        }));
        
        console.log('📄 PDF sent to React Native:', filename);
        return;
      }

      // Regular browser fallback
      doc.save(filename);
    } catch (err) {
      setError('Failed to export PDF');
      console.error('Error exporting PDF:', err);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Delete attendance record
  const deleteAttendance = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.delete(`/attendence/${id}`);
      setAttendanceList(prev => prev.filter(record => record._id !== id));
      toast.success('Attendance record deleted successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete attendance record';
      setError(message);
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    attendanceList,
    managers,
    employees,
    isLoading,
    error,
    fetchAttendance,
    fetchAttendanceByEmployee,
    fetchManagers,
    fetchEmployees,
    fetchLocationWiseAttendance,
    updateAttendance,
    bulkUpdateAttendance,
    deleteAttendance,
    exportToCSV,
    exportToPDF,
    clearError
  };

  return (
    <AttendanceContext.Provider value={value}>
      {children}
    </AttendanceContext.Provider>
  );
};
